import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { NotificationService } from '@shared/services/notification.service';
import { SyncMonitoringService } from '../sync-monitoring.service';
import { buildWorkerCreateRequest, WORKER_FILTER_JOB_TYPE_VALUES, WORKER_FILTER_NAME_TYPE } from '../sync-monitoring.utils';

/** Dialog payload. `environmentId` lets the dialog POST/PUT the worker itself; `environmentName` is display-only. When `worker` is present the dialog is in EDIT mode (prefills + PUTs to that worker's id); when absent it CREATEs a new worker. */
export interface ICreateWorkerDialogData {
	environmentId: string;
	environmentName: string;
	worker?: {
		id: string;
		name: string;
		filters: { name: string; value: string }[];
	};
}

@Component({
	selector: 'app-create-worker-dialog',
	templateUrl: './create-worker-dialog.component.html',
	styleUrls: ['./create-worker-dialog.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class CreateWorkerDialogComponent {
	public isLoading = false;
	public readonly environmentName: string;
	public readonly isEditMode: boolean;
	/** Autocomplete suggestions for a filter's value (the job "Type" tag values core-one emits). */
	public readonly jobTypeValues = WORKER_FILTER_JOB_TYPE_VALUES;
	public workerForm: FormGroup;

	constructor(
		@Inject(MAT_DIALOG_DATA) public data: ICreateWorkerDialogData,
		public dialogRef: MatDialogRef<CreateWorkerDialogComponent, boolean>,
		private fb: FormBuilder,
		private syncMonitoringService: SyncMonitoringService,
		private notificationService: NotificationService,
	) {
		this.environmentName = data.environmentName;
		this.isEditMode = !!data.worker;
		this.workerForm = this.fb.group({
			name: [data.worker?.name ?? '', [Validators.required, Validators.minLength(1), Validators.pattern(/\S/)]],
			// Filters are optional; each added row requires both a name and a value (mirrors the
			// server-side FilterModel, where both are [Required]).
			filters: this.fb.array((data.worker?.filters ?? []).map((filter) => this.buildFilterGroup(filter.name, filter.value))),
		});
	}

	/** Translation key for the dialog title + confirm button, switched by mode. */
	get titleKey(): string {
		return this.isEditMode ? 'administration.sync-monitoring.create-worker-dialog.edit-title' : 'administration.sync-monitoring.create-worker-dialog.title';
	}

	get confirmKey(): string {
		return this.isEditMode ? 'administration.sync-monitoring.create-worker-dialog.save' : 'administration.sync-monitoring.create-worker-dialog.confirm';
	}

	get filters(): FormArray {
		return this.workerForm.controls['filters'] as FormArray;
	}

	get isNameValid(): boolean {
		return this.workerForm.controls['name'].valid;
	}

	/** Filter names must be unique (server enforces [UniqueItems] on FilterModel.Name). */
	get hasDuplicateFilterNames(): boolean {
		const names = this.filters.controls.map((control) => (control.get('name')?.value ?? '').trim().toLowerCase()).filter((name) => name !== '');
		return new Set(names).size !== names.length;
	}

	get canConfirm(): boolean {
		return this.isNameValid && this.filters.valid && !this.hasDuplicateFilterNames && !this.isLoading;
	}

	addFilter(): void {
		this.filters.push(this.buildFilterGroup());
	}

	private buildFilterGroup(name = WORKER_FILTER_NAME_TYPE, value = ''): FormGroup {
		return this.fb.group({
			name: [name, [Validators.required, Validators.minLength(1), Validators.pattern(/\S/)]],
			value: [value, [Validators.required, Validators.minLength(1), Validators.pattern(/\S/)]],
		});
	}

	removeFilter(index: number): void {
		this.filters.removeAt(index);
	}

	onConfirm(): void {
		if (!this.canConfirm) {
			return;
		}

		const request = buildWorkerCreateRequest(
			this.workerForm.controls['name'].value,
			this.filters.controls.map((control) => ({
				name: control.get('name')?.value ?? '',
				value: control.get('value')?.value ?? '',
			})),
		);

		const worker = this.data.worker;
		const save$ = worker ? this.syncMonitoringService.updateWorker(this.data.environmentId, worker.id, request) : this.syncMonitoringService.createWorker(this.data.environmentId, request);
		const errorKey = worker ? 'administration.sync-monitoring.create-worker-dialog.errors.update-failed' : 'administration.sync-monitoring.create-worker-dialog.errors.create-failed';

		this.isLoading = true;
		save$.subscribe({
			next: () => this.dialogRef.close(true),
			error: () => {
				this.isLoading = false;
				this.notificationService.notify([errorKey], [], 'error');
			},
		});
	}

	onCancel(): void {
		this.dialogRef.close();
	}
}
